import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ChatBubbleLeftRightIcon } from '@interstate/components/Icons';
import { StatusIndicator } from '@interstate/components/StatusIndicator';
import { ContextualCommentsPanelContent, useDealCommentsPanel } from '@makemydeal/dr-shared-components';
import { HostEnvironment, hostUrlHelper, locHrefUtil, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { getActiveDealCommonOrgId, getDealXgId, getLaunchUrl, isDealCommentsSVEnabled } from '@makemydeal/dr-sales-checkin-store';
import { DealCommentsPanel, DealCommentsWrapper, StyledDealCommentsButton } from './DealComments.style';

const DealComments = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const dealXgId = useSelector(getDealXgId);
    const commonOrgId = useSelector(getActiveDealCommonOrgId);
    const baseUrl = useSelector(getLaunchUrl);
    const enableDealComments = useSelector(isDealCommentsSVEnabled);

    const { isOpen, showRedDot, close, toggle, commentsElementRef } = useDealCommentsPanel({
        isEnabled: enableDealComments,
        referenceId: dealXgId,
        commonOrgId
    });

    const environment = hostUrlHelper.getHostUrlEnvironment(SALES_CHECKIN_APP_PREFIX, locHrefUtil.getLocHref());
    const isProd = environment === HostEnvironment.Prod || environment === HostEnvironment.DarkProd;
    const partition = isProd ? 'prod' : 'non-prod';

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                close();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, close]);

    const button = (
        <StyledDealCommentsButton
            data-testid="deal-comments-button"
            buttonStyle="tertiary"
            size="small"
            onClick={toggle}
            icon={<ChatBubbleLeftRightIcon />}
        />
    );

    return (
        <DealCommentsWrapper ref={wrapperRef} data-testid="deal-comments-container">
            {showRedDot ? (
                <StatusIndicator variant="dot" data-testid="deal-comments-red-dot">
                    {button}
                </StatusIndicator>
            ) : (
                button
            )}
            <DealCommentsPanel data-testid="deal-comments-panel">
                <ContextualCommentsPanelContent
                    referenceId={dealXgId}
                    commonOrgId={commonOrgId}
                    launchUrl={baseUrl}
                    partition={partition}
                    isOpen={isOpen}
                    onClose={close}
                    commentsElementRef={commentsElementRef}
                />
            </DealCommentsPanel>
        </DealCommentsWrapper>
    );
};

export default DealComments;
